import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import * as styles from './TextConfigurationPanel.module.css';
import * as wizardStyles from './styles.module.css';

/**
 * TextConfigurationPanel Component
 * Provides text editing controls for the Text widget type.
 * Supports: text content, font size, alignment, bold, italic, underline.
 */
function TextConfigurationPanel({ widgetData, onConfigChange }) {
  const { t } = useTranslation();
  const config = widgetData.config || {};

  const handleTextChange = useCallback((e) => {
    onConfigChange('textContent', e.target.value);
  }, [onConfigChange]);

  const handleFontSizeChange = useCallback((e) => {
    onConfigChange('fontSize', parseInt(e.target.value, 10) || 14);
  }, [onConfigChange]);

  const handleAlignmentChange = useCallback((alignment) => {
    onConfigChange('textAlign', alignment);
  }, [onConfigChange]);

  const toggleBold = useCallback(() => {
    onConfigChange('fontWeight', config.fontWeight === 'bold' ? 'normal' : 'bold');
  }, [onConfigChange, config.fontWeight]);

  const toggleItalic = useCallback(() => {
    onConfigChange('fontStyle', config.fontStyle === 'italic' ? 'normal' : 'italic');
  }, [onConfigChange, config.fontStyle]);

  const toggleUnderline = useCallback(() => {
    onConfigChange('textDecoration', config.textDecoration === 'underline' ? 'none' : 'underline');
  }, [onConfigChange, config.textDecoration]);

  return (
    <div className={styles.container}>
      <h2 className={wizardStyles.stepTitle}>{t('dashboard.widgetWizard.textConfig.stepTitle')}</h2>
      <p className={styles.description}>{t('dashboard.widgetWizard.textConfig.description')}</p>

      {/* Formatting Toolbar */}
      <div className={styles.toolbar}>
        {/* Text Style Buttons */}
        <div className={styles.toolbarGroup}>
          <button
            type="button"
            className={`${styles.toolbarButton} ${config.fontWeight === 'bold' ? styles.active : ''}`}
            onClick={toggleBold}
            title={t('dashboard.widgetWizard.textConfig.bold')}
            aria-label={t('dashboard.widgetWizard.textConfig.bold')}
            aria-pressed={config.fontWeight === 'bold'}
          >
            <strong>B</strong>
          </button>
          <button
            type="button"
            className={`${styles.toolbarButton} ${config.fontStyle === 'italic' ? styles.active : ''}`}
            onClick={toggleItalic}
            title={t('dashboard.widgetWizard.textConfig.italic')}
            aria-label={t('dashboard.widgetWizard.textConfig.italic')}
            aria-pressed={config.fontStyle === 'italic'}
          >
            <em>I</em>
          </button>
          <button
            type="button"
            className={`${styles.toolbarButton} ${config.textDecoration === 'underline' ? styles.active : ''}`}
            onClick={toggleUnderline}
            title={t('dashboard.widgetWizard.textConfig.underline')}
            aria-label={t('dashboard.widgetWizard.textConfig.underline')}
            aria-pressed={config.textDecoration === 'underline'}
          >
            <span className={styles.underlineText}>U</span>
          </button>
        </div>

        {/* Separator */}
        <div className={styles.separator} />

        {/* Font Size */}
        <div className={styles.toolbarGroup}>
          <label htmlFor="fontSize" className={styles.toolbarLabel}>
            {t('dashboard.widgetWizard.textConfig.fontSize')}
          </label>
          <select
            id="fontSize"
            className={styles.fontSizeSelect}
            value={config.fontSize || 14}
            onChange={handleFontSizeChange}
          >
            <option value="10">10</option>
            <option value="12">12</option>
            <option value="14">14</option>
            <option value="16">16</option>
            <option value="18">18</option>
            <option value="20">20</option>
            <option value="24">24</option>
            <option value="28">28</option>
            <option value="32">32</option>
            <option value="36">36</option>
            <option value="48">48</option>
          </select>
        </div>

        {/* Separator */}
        <div className={styles.separator} />

        {/* Text Alignment */}
        <div className={styles.toolbarGroup}>
          <button
            type="button"
            className={`${styles.toolbarButton} ${(config.textAlign || 'left') === 'left' ? styles.active : ''}`}
            onClick={() => handleAlignmentChange('left')}
            title={t('dashboard.widgetWizard.textConfig.alignLeft')}
            aria-label={t('dashboard.widgetWizard.textConfig.alignLeft')}
            aria-pressed={(config.textAlign || 'left') === 'left'}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
              <path d="M2 3h12v1H2V3zm0 3h8v1H2V6zm0 3h10v1H2V9zm0 3h6v1H2v-1z" />
            </svg>
          </button>
          <button
            type="button"
            className={`${styles.toolbarButton} ${config.textAlign === 'center' ? styles.active : ''}`}
            onClick={() => handleAlignmentChange('center')}
            title={t('dashboard.widgetWizard.textConfig.alignCenter')}
            aria-label={t('dashboard.widgetWizard.textConfig.alignCenter')}
            aria-pressed={config.textAlign === 'center'}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
              <path d="M2 3h12v1H2V3zm2 3h8v1H4V6zm1 3h6v1H5V9zm2 3h2v1H7v-1z" />
            </svg>
          </button>
          <button
            type="button"
            className={`${styles.toolbarButton} ${config.textAlign === 'right' ? styles.active : ''}`}
            onClick={() => handleAlignmentChange('right')}
            title={t('dashboard.widgetWizard.textConfig.alignRight')}
            aria-label={t('dashboard.widgetWizard.textConfig.alignRight')}
            aria-pressed={config.textAlign === 'right'}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
              <path d="M2 3h12v1H2V3zm4 3h8v1H6V6zm2 3h6v1H8V9zm4 3h2v1h-2v-1z" />
            </svg>
          </button>
        </div>
      </div>

      {/* Text Editor */}
      <div className={styles.editorContainer}>
        <textarea
          className={styles.textEditor}
          value={config.textContent || ''}
          onChange={handleTextChange}
          placeholder={t('dashboard.widgetWizard.textConfig.placeholder')}
          style={{
            fontSize: `${config.fontSize || 14}px`,
            textAlign: config.textAlign || 'left',
            fontWeight: config.fontWeight || 'normal',
            fontStyle: config.fontStyle || 'normal',
            textDecoration: config.textDecoration || 'none',
          }}
        />
      </div>
    </div>
  );
}

TextConfigurationPanel.propTypes = {
  widgetData: PropTypes.shape({
    config: PropTypes.shape({
      textContent: PropTypes.string,
      fontSize: PropTypes.number,
      textAlign: PropTypes.oneOf(['left', 'center', 'right']),
      fontWeight: PropTypes.oneOf(['normal', 'bold']),
      fontStyle: PropTypes.oneOf(['normal', 'italic']),
      textDecoration: PropTypes.oneOf(['none', 'underline']),
    }),
  }).isRequired,
  onConfigChange: PropTypes.func.isRequired,
};

export default TextConfigurationPanel;

